import React, { useState, useEffect, useRef } from 'react';
import { T, Th, Tr, ActionBtn, Modal, TableCard, TableBar, SearchInput, Toast, PrimaryBtn, visibilityBadge } from './theme';
import { useRefresh } from './RefreshContext';
import API_BASE_URL from '../config/api';

const MyEventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [viewEvent, setViewEvent] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', event_start_date: '', event_end_date: '', event_details: '' });
  const [editImageFile, setEditImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const editFileRef = useRef(null);
  const userEmail = localStorage.getItem('vrvEmail') || '';
  const { refresh, triggerRefresh } = useRefresh();

  const fetchMyEvents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-events-getAll`);
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setEvents((parsed.data || []).filter(e => (e.createdBy || '').toLowerCase() === userEmail.toLowerCase()).sort((a, b) => new Date(b.event_start_date || 0) - new Date(a.event_start_date || 0)));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchMyEvents(); }, [userEmail, refresh]); // eslint-disable-line

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const filtered = events.filter(e => !search || Object.values(e).some(v => String(v).toLowerCase().includes(search.toLowerCase())));
  const toggleSelect = id => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map(e => e.event_id));

  const handleBulkAction = async (action) => {
    if (!selectedIds.length) { showMsg('Select at least one item', 'error'); return; }
    try {
      const token = localStorage.getItem('vrvToken');
      let ok = true;
      for (const event_id of selectedIds) {
        const res = await fetch(`${API_BASE_URL}/vrv-events-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ event_id, action }) });
        if (!res.ok) ok = false;
      }
      showMsg(ok ? `${action} applied to ${selectedIds.length} item(s)` : 'Some failed', ok ? 'success' : 'error');
      setSelectedIds([]); fetchMyEvents(); triggerRefresh();
    } catch { showMsg('Error connecting', 'error'); }
  };

  const openEdit = (ev) => { setEditEvent(ev); setEditForm({ title: ev.title || '', event_start_date: ev.event_start_date || '', event_end_date: ev.event_end_date || '', event_details: ev.event_details || '' }); setEditImageFile(null); };
  const hasChanges = () => editEvent && (editForm.title !== (editEvent.title || '') || editForm.event_start_date !== (editEvent.event_start_date || '') || editForm.event_end_date !== (editEvent.event_end_date || '') || editForm.event_details !== (editEvent.event_details || '') || editImageFile !== null);

  const handleEditSave = async () => {
    if (!editForm.title || !editForm.event_start_date || !editForm.event_end_date || !editForm.event_details) { showMsg('All fields required', 'error'); return; }
    if (!hasChanges()) { showMsg('No changes to save', 'error'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE_URL}/vrv-events-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ event_id: editEvent.event_id, action: 'UPDATE', ...editForm }) });
      if (editImageFile && res.ok) {
        const urlRes = await fetch(`${API_BASE_URL}/vrv-events-generate-upload-url?event_id=${editEvent.event_id}&fileName=${editImageFile.name}`, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
        const urlData = await urlRes.json();
        const parsed = typeof urlData.body === 'string' ? JSON.parse(urlData.body) : urlData;
        if (parsed.uploadUrl) {
          await fetch(parsed.uploadUrl, { method: 'PUT', headers: { 'Content-Type': editImageFile.type }, body: editImageFile });
          if (parsed.imageUrl) await fetch(`${API_BASE_URL}/vrv-events-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ event_id: editEvent.event_id, action: 'UPDATE_IMAGE', event_image_url: parsed.imageUrl + '?v=' + Date.now() }) });
        }
      }
      if (res.ok) { showMsg('Event updated!', 'success'); setEditEvent(null); fetchMyEvents(); triggerRefresh(); }
      else showMsg('Update failed', 'error');
    } catch { showMsg('Error connecting', 'error'); }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-16"><p className="text-sm" style={{ color: T.muted }}>Loading…</p></div>;
  if (!events.length) return (
    <div className="vrv-card text-center py-16">
      <div className="text-4xl mb-3">📅</div>
      <p className="font-semibold" style={{ color: T.muted }}>No events created by you yet.</p>
    </div>
  );

  return (
    <div>
      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>

      {/* Desktop Table */}
      <div className="desktop-table">
        <TableCard>
          <TableBar className="table-bar-wrap">
            <p className="text-xs font-bold" style={{ color: T.muted }}>{selectedIds.length > 0 ? `${selectedIds.length} selected` : `${filtered.length} events`}</p>
            <div className="flex flex-wrap gap-2">
              {[['Public', 'gold'], ['Private', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={!selectedIds.length} color={color}>{label}</ActionBtn>
              ))}
            </div>
          </TableBar>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr>
                <th className="px-3 py-3 w-8" style={{ background: T.stripe, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0} onChange={toggleAll} />
                </th>
                <Th>Title</Th><Th>Start Date</Th><Th>End Date</Th><Th>Visibility</Th><Th center>View</Th><Th center>Edit</Th>
              </tr></thead>
              <tbody>
                {filtered.map((ev, i) => (
                  <Tr key={ev.event_id || i} selected={selectedIds.includes(ev.event_id)}>
                    <td className="px-3 py-2.5"><input type="checkbox" checked={selectedIds.includes(ev.event_id)} onChange={() => toggleSelect(ev.event_id)} /></td>
                    <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{ev.title || '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: T.muted }}>{ev.event_start_date ? new Date(ev.event_start_date).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: T.muted }}>{ev.event_end_date ? new Date(ev.event_end_date).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={visibilityBadge(ev.visibility)}>{ev.visibility}</span></td>
                    <td className="px-3 py-2.5 text-center"><ActionBtn onClick={() => setViewEvent(ev)} color="muted" small>View</ActionBtn></td>
                    <td className="px-3 py-2.5 text-center"><ActionBtn onClick={() => openEdit(ev)} color="gold" small>Edit</ActionBtn></td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        </TableCard>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        <div className="flex flex-wrap gap-2 mb-3">
          {[['Public', 'gold'], ['Private', 'muted'], ['Delete', 'red']].map(([label, color]) => (
            <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={!selectedIds.length} color={color}>{label}</ActionBtn>
          ))}
        </div>
        {filtered.map((ev, i) => (
          <div key={ev.event_id || i} className="vrv-mobile-card" style={{ border: selectedIds.includes(ev.event_id) ? `1.5px solid ${T.gold}` : undefined }}>
            <div className="flex items-start gap-3 mb-2">
              <input type="checkbox" checked={selectedIds.includes(ev.event_id)} onChange={() => toggleSelect(ev.event_id)} className="mt-1" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate" style={{ color: T.navy }}>{ev.title || '—'}</p>
                <p className="text-xs" style={{ color: T.muted }}>{ev.event_start_date ? new Date(ev.event_start_date).toLocaleDateString() : '—'} – {ev.event_end_date ? new Date(ev.event_end_date).toLocaleDateString() : '—'}</p>
              </div>
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase flex-shrink-0" style={visibilityBadge(ev.visibility)}>{ev.visibility}</span>
            </div>
            <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <ActionBtn onClick={() => setViewEvent(ev)} color="muted" small>View</ActionBtn>
              <ActionBtn onClick={() => openEdit(ev)} color="gold" small>Edit</ActionBtn>
            </div>
          </div>
        ))}
      </div>

      {viewEvent && (
        <Modal onClose={() => setViewEvent(null)} title={viewEvent.title} subtitle="Event Preview" wide>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[['Start Date', viewEvent.event_start_date ? new Date(viewEvent.event_start_date).toLocaleString() : '—'], ['End Date', viewEvent.event_end_date ? new Date(viewEvent.event_end_date).toLocaleString() : '—']].map(([label, val]) => (
              <div key={label}>
                <label className="vrv-label">{label}</label>
                <p className="text-sm px-3 py-2.5 rounded-xl" style={{ background: '#F8F5EE', color: T.ink, border: '1px solid rgba(0,0,0,0.07)' }}>{val}</p>
              </div>
            ))}
          </div>
          {viewEvent.event_image_url && <img src={viewEvent.event_image_url} alt={viewEvent.title} className="w-full max-h-64 object-contain rounded-2xl mb-4" style={{ border: '1px solid rgba(0,0,0,0.07)' }} />}
          {viewEvent.event_details && <p className="text-sm leading-relaxed whitespace-pre-wrap mb-6 pt-4" style={{ color: T.muted, borderTop: '1px solid rgba(0,0,0,0.06)' }}>{viewEvent.event_details}</p>}
          <PrimaryBtn onClick={() => setViewEvent(null)}>Close</PrimaryBtn>
        </Modal>
      )}

      {editEvent && (
        <Modal onClose={() => setEditEvent(null)} title="Edit Event" subtitle="My Content" wide>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="vrv-label">Title *</label>
              <input type="text" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className="vrv-input" />
            </div>
            <div>
              <label className="vrv-label">Start Date *</label>
              <input type="datetime-local" value={editForm.event_start_date} onChange={e => setEditForm({ ...editForm, event_start_date: e.target.value })} min={new Date().toISOString().slice(0,10) + 'T00:00'} className="vrv-input" />
            </div>
            <div>
              <label className="vrv-label">End Date *</label>
              <input type="datetime-local" value={editForm.event_end_date} onChange={e => setEditForm({ ...editForm, event_end_date: e.target.value })} className="vrv-input" />
            </div>
            <div className="md:col-span-2">
              <label className="vrv-label">Replace Image</label>
              {editEvent.event_image_url && !editImageFile && <img src={editEvent.event_image_url} alt="Current" className="h-20 object-contain rounded-xl mb-2" />}
              {editImageFile && <img src={URL.createObjectURL(editImageFile)} alt="New" className="h-20 object-contain rounded-xl mb-2" />}
              <input ref={editFileRef} type="file" accept="image/*" onChange={e => setEditImageFile(e.target.files[0])} className="vrv-input" />
            </div>
            <div className="md:col-span-2">
              <label className="vrv-label">Event Details *</label>
              <textarea value={editForm.event_details} onChange={e => setEditForm({ ...editForm, event_details: e.target.value })} rows="4" className="vrv-input resize-none" />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <ActionBtn onClick={() => setEditEvent(null)} color="muted">Cancel</ActionBtn>
            <button onClick={handleEditSave} disabled={saving || !hasChanges()} className="btn-navy" style={{ opacity: saving || !hasChanges() ? 0.45 : 1 }}>
              {saving ? 'Saving…' : 'Update Event →'}
            </button>
          </div>
        </Modal>
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default MyEventsSection;
